/**
 * Question World - Main Page
 * Displays all instant practice exams for the student
 */

import React, { useEffect, useState } from 'react';
import { Layout, Typography, Row, Col, Spin, Empty } from 'antd';
import { ReadOutlined } from '@ant-design/icons';

import ExamCard from './components/ExamCard';
import FilterComponent from './components/FilterComponent';
import { fetchInstantPracticeExams } from './helpers/api';
import { filterExams, resetFilters } from './helpers/questionFilter';
import './styles/question_world.css';

// Import shared header components
import StudentHeader from '../../../Header/student/studentHeader.component';
import SecondaryHeaderComponent from '../../../Header/student/secondaryHeader.component';
import StudentTabMenu from '../../../TabMenu/TabMenu';
import { getTabMenuData } from '../tabMenuData';

const { Content, Header } = Layout;
const { Title, Text } = Typography;

/**
 * QuestionWorldPage - Main page component
 * Shows all instant practice exams grouped by course
 */
const QuestionWorldPage = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [instantPracticeExams, setInstantPracticeExams] = useState([]);
    const [allExams, setAllExams] = useState([]); // Store all exams for filtering
    const [selectedCourse, setSelectedCourse] = useState(null);
    // const [selectedExam, setSelectedExam] = useState(null); // Commented for future use
    const [selectedSubject, setSelectedSubject] = useState(null);

    // Get tab menu data (Question World is always included on this page)
    const tabMenuData = getTabMenuData(true);

    // Fetch instant practice exams from API
    useEffect(() => {
        let isMounted = true;

        const loadExams = async () => {
            if (isMounted) setIsLoading(true);
            try {
                const exams = await fetchInstantPracticeExams();
                if (isMounted) {
                    setAllExams(exams);
                    setInstantPracticeExams(exams);
                }
            } catch (error) {
                console.error('Error loading instant practice exams:', error);
                if (isMounted) {
                    setInstantPracticeExams([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadExams();

        return () => {
            isMounted = false;
        };
    }, []);

    // Filter exams when filters change
    useEffect(() => {
        const filtered = filterExams(allExams, selectedCourse, null, selectedSubject); // selectedExam commented
        setInstantPracticeExams(filtered);
    }, [allExams, selectedCourse, selectedSubject]); // selectedExam removed from dependencies

    // Handle course change
    const handleCourseChange = (courseId) => {
        // If no exam is selected, use resetFilters to reset exam and subject
        const updatedFilters = resetFilters('course', {
            selectedCourse: courseId,
            selectedExam: null, // Commented for future use
            selectedSubject
        });
        setSelectedCourse(updatedFilters.selectedCourse);
        // setSelectedExam(updatedFilters.selectedExam); // Commented for future use
        setSelectedSubject(updatedFilters.selectedSubject);
    };

    // Handle exam change - Commented for future use
    // const handleExamChange = (examId) => {
    //     const updatedFilters = resetFilters('exam', {
    //         selectedCourse,
    //         selectedExam: examId,
    //         selectedSubject
    //     });
    //     setSelectedCourse(updatedFilters.selectedCourse);
    //     setSelectedExam(updatedFilters.selectedExam);
    //     setSelectedSubject(updatedFilters.selectedSubject);
    // };

    // Handle subject change
    const handleSubjectChange = (subjectId) => {
        // If course is already selected, keep it and just set the subject
        // This allows filtering by both courseId and subjectId
        if (selectedCourse) {
            setSelectedSubject(subjectId);
            // Keep course selected - don't reset it
        } else {
            // If no course is selected, use resetFilters to reset course and exam
            const updatedFilters = resetFilters('subject', {
                selectedCourse,
                selectedExam: null, // Commented for future use
                selectedSubject: subjectId
            });
            setSelectedCourse(updatedFilters.selectedCourse);
            // setSelectedExam(updatedFilters.selectedExam); // Commented for future use
            setSelectedSubject(updatedFilters.selectedSubject);
        }
    };

    // Handle clear all filters
    const handleClearFilters = () => {
        setSelectedCourse(null);
        // setSelectedExam(null); // Commented for future use
        setSelectedSubject(null);
    };

    return (
        <Layout>
            <Header>
                <StudentHeader />
            </Header>
            <Layout>
                <SecondaryHeaderComponent />
            </Layout>
            <Layout>
                <Content className="custom-container section-padding sec-mh">
                    {/* Tab Menu */}
                    <Row style={{ marginRight: 0, marginLeft: 0 }}>
                        <Col xs={24}>
                            <div className="desktop-student-tab">
                                <StudentTabMenu selectedKey="question-world" menuData={tabMenuData} />
                            </div>
                        </Col>
                    </Row>

                    {/* Page Header */}
                    <div className="qw-header" style={{ marginTop: 20 }}>
                        <Row align="middle" gutter={16} justify="space-between">
                            <Col flex="none">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <ReadOutlined style={{ fontSize: 32, color: '#662d91' }} />
                                    <div>
                                        <Title level={3} className="qw-title" style={{ marginBottom: 0 }}>
                                            Question World
                                        </Title>
                                        <Text className="qw-subtitle">
                                            Practice questions with instant feedback
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                            <Col md={24} xs={24} sm={24} lg={12}>
                                <div style={{ backgroundColor: '#f0f2f5', padding: 10, borderRadius: 10 }}>
                                    <FilterComponent
                                        exams={allExams}
                                        selectedCourse={selectedCourse}
                                        selectedExam={null} // Commented for future use
                                        selectedSubject={selectedSubject}
                                        onCourseChange={handleCourseChange}
                                        onExamChange={() => {}} // Commented for future use
                                        onSubjectChange={handleSubjectChange}
                                        onClearFilters={handleClearFilters}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Loading State */}
                    {isLoading && (
                        <div className="qw-loading">
                            <Spin size="large" tip="Loading exams..." />
                        </div>
                    )}

                    {/* Empty State */}
                    {!isLoading && instantPracticeExams.length === 0 && (
                        <div className="qw-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Text className="qw-empty-text">
                                        No instant practice exams available
                                    </Text>
                                }
                            />
                        </div>
                    )}

                    {/* Exam Cards Grid */}
                    {!isLoading && instantPracticeExams.length > 0 && (
                        <Row gutter={[16, 16]} style={{ marginTop: 20, paddingBottom: 60 }}>
                            {instantPracticeExams.map((exam, index) => (
                                <Col key={exam?.id || index} xs={24} sm={12} md={8} lg={6}>
                                    <ExamCard
                                        exam={{
                                            examId: {
                                                _id: exam.id,
                                                title: exam.exam_name,
                                                questions: Array(exam.question_count).fill(null), // Create array for length calculation
                                                isSegmentedExam: false,
                                            },
                                            subject_info: exam.subject_info,
                                            course_info: exam.course_info,
                                        }}
                                        groupId={exam?.group_id}
                                        groupName={exam?.group_name}
                                        pageType="question-world"
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </Content>
            </Layout>
        </Layout>
    );
};

export default QuestionWorldPage;

